import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import useTopScrollbar from '../hooks/useTopScrollbar';
import useMatchedWidths from '../hooks/useMatchedWidths';
import { format } from 'date-fns';
import { useConfig } from '../context/ConfigContext';
import { getPhToday } from '../utils/phTime';
import { sortTimeSlots, isSlotPassed, formatTimeSlotRange, formatTimeSlotRangeParts, parseTimeSlotStartHour, getCourtLabel, isCourtAvailable, isAddOnCourt, getTimeSlotsForDate, getTimeSlotsForCourt, getGridRowsForDate, isPastDateViewEnabled, getEnabledGridStatuses, isCloseReasonPromptEnabled, getRateBandForSlot } from '../utils/bookingUtils';
// Config -> Sports & Courts -> Hourly rates: a separator row where each rate starts.
import {
  planRateSeparators,
  isRateSeparatorShown,
  resolveRateSeparatorDesign,
  formatRateBandHours,
  getRateSeparatorPriceText,
} from '../utils/rateSeparatorUtil';
import RateSeparatorRow from './RateSeparatorRow';
import { isPackageBooking, getPackageBookingCourts, describePackageBookingTime } from '../utils/packageUtils';
import { subscribeOpenPlayDay, getSessionCourtNumbers, getSessionTimeRangeInfo } from '../utils/openPlayUtil';
import { SLOT_STATUS_CLOSED, GRID_STATUS_PARTIAL, GRID_BOOKED_CELL_STATUSES, ADMIN_GRID_STATUS_OPTIONS } from '../constants/bookingConst';
import { hasActivePromo } from '../utils/promoUtils';
// A booked cell whose reservation still owes money carries a small marker,
// so staff can see it without opening the booking (see AdminBooking.jsx's
// Manage Booking modal for the figures behind it).
import { getBookingPaymentState } from '../utils/partialPaymentUtil';
import AdminHostOpenPlayModal from './AdminHostOpenPlayModal';
import OpenPlaySessionsSection from './OpenPlaySessionsSection';
// Warns when Shared Ownership is on but a court has no owner, or an owner
// has no QR — both make courts unbookable for guests.
import OwnershipWarningBanner from './OwnershipWarningBanner';
import CellInitialsInput from './CellInitialsInput';
// Asks why an hour is being closed before the Closed status is staged.
import CloseSlotReasonModal from './CloseSlotReasonModal';
// "Generate images" — shareable PNGs of the selected day's open slots.
import AvailabilityImageModal from './AvailabilityImageModal';
import { buildAvailabilitySnapshot } from '../utils/availabilitySnapshotUtil';
import { loadAvailabilitySnapshotsForRange } from '../utils/availabilityRangeUtil';
import { getCloseReason } from '../utils/slotCloseReasonUtil';
// Config -> Booking Settings: block bookings render as one merged cell.
import { buildBlockSpans, describeBlockRange } from '../utils/bookingRulesUtil';
import '../css/AdminTimeSlotSelection.css';
import { formatMoney } from '../utils/currencyUtil';
import { hasReceipt } from '../utils/receiptUtil';

/**
 * What kind of thing a Reservation Queue card represents.
 *
 * The queue mixes three things that need different judgement — a court for
 * an hour, a whole-venue package block, and players joining an Open Play
 * session — and they were indistinguishable until the admin opened one.
 *
 * @param {object} booking - a queue entry
 * @returns {{label: string, cls: string}}
 */
const queueCardType = (booking) => {
  if (booking?.isOpenPlayJoin) return { label: 'Open Play', cls: 'openplay' };
  if (isPackageBooking(booking)) return { label: 'Package', cls: 'package' };
  return { label: 'Court Booking', cls: 'court' };
};

const WINDOW = 14; // days shown per chip-strip page — same pattern as the guest TimeSlotSelection screen
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DOW_S = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON_S = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const sameYMD = (a, b) => !!a && !!b && dateKey(a) === dateKey(b);
const diffDays = (a, b) => Math.round((a.getTime() - b.getTime()) / 86400000);

// The availability statuses an admin can set per hour/court, in the same
// order they appear in the jassen reference dropdown (admin-timeslot2.png).
// Every option is always selectable — the admin can set a cell to any
// status regardless of what it currently shows.
//
// 'openPlay' is deliberately NOT offered here anymore — Open Play is now
// always hosted/edited as a whole SESSION (possibly spanning multiple
// courts and time slots) via "Host an Open Play session" / the
// "Hosted open play sessions" cards below the grid (see
// OpenPlaySessionsSection.jsx), never one cell at a time. A cell already
// showing Open Play renders as a locked, non-interactive badge instead of
// this dropdown — see the grid render below.
//
// 'closed' takes ONE hour on ONE court out of service. It is the per-cell
// counterpart of switching a whole court off in Config -> Sports & Courts,
// and the guest grid blocks it identically — greyed, hatched, unclickable —
// but labelled "CLOSED" rather than "Unavailable", so a guest can tell one
// closed hour from a court that is out of service entirely. Unlike 'passed' or
// 'processing', which are admin-side bookkeeping, this one is visible to
// guests, so it also gets re-checked at checkout (App.jsx).
//
// 'partial' is the odd one out: `selectable: false` renders it disabled, so
// it can be DISPLAYED as a cell's current status but never picked. A cell
// becomes partial by someone taking a downpayment on its reservation
// (Confirm (Partial)), and stops being one when the balance is settled —
// letting the dropdown set it would claim money had been received that
// nobody recorded, and there would be no amount to record it as.
//
// The list itself lives in bookingConst.js (ADMIN_GRID_STATUS_OPTIONS) so
// Config -> System can offer a checkbox per option; the dropdown only shows
// the ticked ones (see getEnabledGridStatuses).
const STATUS_OPTIONS = ADMIN_GRID_STATUS_OPTIONS;

// The same statuses on an add-on column (Config -> Sports & Courts -> Court
// type "Add-on"), except that a free hour there reads "Add" rather than
// "Open" — an add-on is an extra a guest attaches to a court hour, not a
// court to open for booking, and the guest grid words it that way too.
// Only the LABEL differs: the stored value stays 'open', so saving, the
// cell's styling and every status comparison behave identically.
const ADDON_STATUS_OPTIONS = STATUS_OPTIONS.map(opt => (
  opt.value === 'open' ? { ...opt, label: 'Add' } : opt
));

// Guest initials for the small left-hand badge in each cell (e.g. "Juan
// Dela Cruz" -> "JD"), matching the jassen reference's "TB" / "LP" style.
const getInitials = (booking) => {
  if (!booking?.userName) return '--';
  const parts = booking.userName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '--';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0][1] || '');
  const initials = `${first}${last}`.toUpperCase();
  return initials || '--';
};

// Keyed by DATE as well as hour and court: a grid that runs past midnight
// shows two dates at once, and "1-2AM__1" alone would be the same key for
// tomorrow's 1AM cell and (on the next day's grid) its own.
const cellKey = (dateStr, timeSlot, court) => `${dateStr}__${timeSlot}__${court}`;

/**
 * "Sat, Sep 12" — the date under an hour that has crossed midnight.
 * Parsed by field, not `new Date(str)`, which reads a bare date as UTC.
 * @param {string} dateStr - 'yyyy-MM-dd'
 * @returns {string}
 */
const formatNextDayLabel = (dateStr) => {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const QUEUE_PAGE_SIZE = 5;

// Compact "5PM" / "12MN" (midnight) / "12NN" (noon) hour label, used for
// the single-line schedule range in the "Copy Details" export.
const formatCompactHour = (hour24) => {
  const h = ((hour24 % 24) + 24) % 24;
  if (h === 0) return '12MN';
  if (h === 12) return '12NN';
  return h < 12 ? `${h}AM` : `${h - 12}PM`;
};

// The hour a booking starts, for ordering the Copy Details export
// chronologically. Records with no slots sort last rather than poisoning
// the comparator with NaN.
const firstSlotHour = (booking) => {
  const slots = sortTimeSlots(Object.keys(booking?.timeSlots || {}));
  const hour = slots.length ? parseTimeSlotStartHour(slots[0]) : NaN;
  return Number.isFinite(hour) ? hour : 99;
};

// Combines a booking's individual hour slots into one range, e.g.
// ["6-7PM", "7-8PM", "8-9PM"] -> "6PM – 9PM".
const formatBookingSchedule = (booking) => {
  // An event package is sold as one block at one price, and its timeSlots
  // also hold the club's cleanup hours — quoting those here would put hours
  // on the run sheet that the guest was never sold. Described the same way
  // every other package recap describes it (see PackageBookingBox.jsx).
  if (isPackageBooking(booking)) return describePackageBookingTime(booking);
  const slots = sortTimeSlots(Object.keys(booking?.timeSlots || {}));
  if (slots.length === 0) return '—';
  const startHour = parseTimeSlotStartHour(slots[0]);
  const endHour = parseTimeSlotStartHour(slots[slots.length - 1]) + 1;
  return `${formatCompactHour(startHour)} – ${formatCompactHour(endHour)}`;
};

// Court numbers as one label for the "Copy Details" export — e.g. "Court 1" /
// "Courts 1, 2", or the admin's own court names once they've set any. Falls
// back to a bare "Court" label when the venue only has one court, where
// naming it adds nothing. Shared by court bookings and Open Play sessions,
// which store their courts differently but read the same on the run sheet.
const formatCourtNumbers = (courtNumbers, numberOfCourts, config) => {
  if ((numberOfCourts || 1) <= 1) return 'Court';
  const nums = [...new Set((courtNumbers || []).map(Number).filter(Number.isFinite))].sort((a, b) => a - b);
  if (nums.length === 0) return 'Court';

  const labels = nums.map(n => getCourtLabel(config, n));
  // Only collapse to the compact "Courts 1, 2" form while every court is
  // still using its default label — custom names have to be spelled out.
  const allDefault = labels.every((label, i) => label === `Court ${nums[i]}`);
  if (!allDefault) return labels.join(', ');
  return nums.length > 1 ? `Courts ${nums.join(', ')}` : `Court ${nums[0]}`;
};

// Distinct courts reserved across all of a booking's slots.
const formatBookingCourts = (booking, numberOfCourts, config) => {
  // Package bookings read their courts off the hours actually sold rather
  // than off every timeSlots key, which also covers the cleanup slots.
  const nums = isPackageBooking(booking)
    ? getPackageBookingCourts(booking)
    : Object.values(booking?.timeSlots || {}).flatMap(c => (Array.isArray(c) ? c : Object.keys(c).map(Number)));
  return formatCourtNumbers(nums, numberOfCourts, config);
};

// An Open Play session's overall hour range. Courts can each run their own
// start/end (see AdminHostOpenPlayModal's per-court picker), so the span is
// flagged when it isn't the same on every court rather than quoting an
// outer range that no single court actually keeps.
const formatSessionSchedule = (session) => {
  const { startHour, endHour, variesByCourt, endsNextDay } = getSessionTimeRangeInfo(session);
  if (startHour === null) return '—';
  // Compact hours here ("10PM – 6AM"), so the "+1" marker has to be compact
  // too — this string sits on one line of a run-sheet export.
  const range = `${formatCompactHour(startHour)} – ${formatCompactHour(endHour)}${endsNextDay ? ' (+1 day)' : ''}`;
  return variesByCourt ? `${range} (varies by court)` : range;
};

// A booking's equipment rental as one line — "Paddle x1, Training Balls
// (set) x2". Kept local to this export: the rental otherwise reaches the run
// sheet as nothing at all, since the copied block carries no amounts to
// attribute it to. Reads `qty`, the field EquipmentRentalModal writes.
//
// Every item carries its count, including "x1". Whoever hands the equipment
// out is counting pieces off this line, and a bare "Paddle" mixed in among
// "Training Balls (set) x2" reads as an unstated quantity rather than as
// exactly one — especially where an item's own name ends in a number.
const formatBookingEquipment = (booking) => (booking?.equipmentRental?.items || [])
  .map((item) => {
    const label = item?.label || item?.name || item?.id || 'Item';
    // A missing/0 qty still means one physical item was rented — the row
    // only exists because the guest picked it.
    const quantity = Number(item?.qty) || 1;
    return `${label} x${quantity}`;
  })
  .filter(Boolean)
  .join(', ');

const AdminTimeSlotSelection = forwardRef(function AdminTimeSlotSelection({
  selectedDate,
  onDateSelect,
  bookedSlots,
  slotOverrides,
  // The following date's bookings and overrides, feeding the after-midnight
  // rows (Config -> Pricing -> Open Past Midnight Until). Those rows are real
  // next-date cells and must not read this date's maps.
  nextDayBookedSlots = {},
  nextDaySlotOverrides = {},
  // Why each CLOSED hour was closed (slotCloseReasons/{date}), for the cell
  // tooltip — this date's and the following date's, like the overrides.
  slotCloseReasons = {},
  nextDaySlotCloseReasons = {},
  pendingQueue,
  onManageBooking,
  onSaveChanges,
  onQueueCardClick,
}, ref) {
  const { config } = useConfig();
  // Config -> System -> Open Play. Off for every club that hasn't opted in,
  // including any config saved before the setting existed.
  const openPlayEnabled = config?.openPlayEnabled === true;
  const today = useMemo(() => getPhToday(), []);
  const jumpInputRef = useRef(null);

  const [winStart, setWinStart] = useState(0);
  const [pendingChanges, setPendingChanges] = useState({});
  // The cell waiting on a close reason ({ rowDate, timeSlot, court, booking })
  // while CloseSlotReasonModal is open; null otherwise.
  const [closePrompt, setClosePrompt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  // The day's availability as frozen when "Generate images" was pressed, or
  // null while that dialog is closed.
  const [availabilitySnapshot, setAvailabilitySnapshot] = useState(null);
  // Open Play for the selected date (sessions + their joins). Open Play lives
  // in its own Firebase collections, not in `bookings`, so `bookedSlots`
  // never carries it — without this listener the day summary silently
  // omitted every Open Play player on the schedule.
  const [openPlayDay, setOpenPlayDay] = useState([]);
  const [queuePage, setQueuePage] = useState(1);
  // "Current Date" shows only the selected day's pending reservations;
  // "All Dates" shows every pending reservation regardless of day.
  //
  // Defaults to 'all': the queue is a to-do list of reservations waiting on
  // the admin, and one sitting on another date is just as much waiting. With
  // 'current' the count in the header could read 5 while the list below it
  // showed none, and nothing on screen said the day filter was why.
  const [queueDateFilter, setQueueDateFilter] = useState('all');
  // "Host an Open Play session" — the only way to create or edit Open Play
  // now (see AdminHostOpenPlayModal.jsx and the STATUS_OPTIONS comment
  // above). `editingOpenPlaySession` is null while hosting a brand new
  // session, or the clicked session record while editing one via its card
  // in the "Hosted open play sessions" list below the grid.
  const [showHostOpenPlay, setShowHostOpenPlay] = useState(false);
  const [editingOpenPlaySession, setEditingOpenPlaySession] = useState(null);
  // Which sport's courts the grid is showing — only relevant once the club
  // has more than one sport configured (see multiSportGrid below). Defaults
  // to the first configured sport and re-syncs if that sport gets removed
  // in Config -> Sports & Courts while this screen is open.
  const [activeSportId, setActiveSportId] = useState(null);
  useEffect(() => {
    const sports = config?.sports || [];
    if (sports.length === 0) return;
    if (!activeSportId || !sports.some(s => s.id === activeSportId)) {
      setActiveSportId(sports[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.sports]);

  // Keep the visible 14-day window in sync with whatever date is selected.
  useEffect(() => {
    if (!selectedDate) return;
    const diff = diffDays(selectedDate, today);
    // Negative for a past date — the strip pages backwards so the admin can
    // edit days that have already gone.
    setWinStart(Math.floor(diff / WINDOW) * WINDOW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate ? dateKey(selectedDate) : null]);

  // Any un-saved edits belong to whichever date they were made on — clear
  // them when the admin switches days rather than silently carrying them
  // over (and possibly applying them to the wrong date).
  useEffect(() => {
    setPendingChanges({});
  }, [selectedDate ? dateKey(selectedDate) : null]);

  useEffect(() => {
    if (!saveMessage) return;
    const t = setTimeout(() => setSaveMessage(''), 3000);
    return () => clearTimeout(t);
  }, [saveMessage]);

  useEffect(() => {
    if (!copyMessage) return;
    const t = setTimeout(() => setCopyMessage(''), 3000);
    return () => clearTimeout(t);
  }, [copyMessage]);

  // Real court numbers from Config -> Sports & Courts (may be non-sequential
  // once an admin adds/removes courts across multiple sports), falling back
  // to the old 1..numberOfCourts sequence for a config that hasn't been
  // migrated to config.courts yet. Mirrors the same fallback TimeSlotSelection.jsx
  // (the guest-facing grid) uses.
  const multiSportGrid = (config?.sports?.length || 0) > 1;
  const allCourts = (config?.courts && config.courts.length > 0)
    ? config.courts.map(c => Number(c.number)).sort((a, b) => a - b)
    : Array.from({ length: config?.numberOfCourts || 0 }, (_, i) => i + 1);
  // Once there's more than one sport, the grid shows only the active tab's
  // courts — court numbers stay globally unique across sports (see
  // AdminConfig.jsx's handleAddCourt), so this is a plain filter, not a
  // separate lookup. A single-sport club never sees the tab bar and always
  // gets the full court list.
  const courts = multiSportGrid
    ? allCourts.filter(court => (config?.courts || []).find(c => Number(c.number) === court)?.sportId === activeSportId)
    : allCourts;
  // Per date, for the same reason the guest grid is: a club can close
  // earlier at weekends, and the admin grid has to show the same day the
  // guest is being offered or the two disagree about what exists.
  // Scoped to the courts on screen and unioned across them, exactly as the
  // guest grid does it — a set scoped to particular courts must not make the
  // two grids disagree about which hours exist.
  // The rows the grid draws: this date's hours, then the next date's early
  // ones for a club trading past midnight. Each row carries its own date, and
  // every read and write below uses it — an after-midnight row is a NEXT-DATE
  // cell, not a relabelled one.
  const gridRows = useMemo(
    () => getGridRowsForDate(config, selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null, courts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.timeSlots, config?.timeSlotSets, config?.courts, config?.nextDayGridEndHour, selectedDate, courts.join(',')],
  );
  const viewedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  // Per-court hours for the next date, so an after-midnight row can read
  // CLOSED on a court exactly as any other row can.
  const nextDayCourtSlots = useMemo(() => {
    const nextRow = gridRows.find(r => r.dayOffset === 1);
    if (!nextRow) return {};
    return Object.fromEntries(courts.map(court => [
      court,
      new Set(getTimeSlotsForCourt(config, nextRow.dateStr, court)),
    ]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.timeSlots, config?.timeSlotSets, gridRows, courts.join(',')]);
  /** The bookings map a row should read. @param {string} rowDate */
  const bookedSlotsFor = (rowDate) => (rowDate === viewedDateStr ? bookedSlots : nextDayBookedSlots) || {};
  /** The overrides map a row should read. @param {string} rowDate */
  const overridesFor = (rowDate) => (rowDate === viewedDateStr ? slotOverrides : nextDaySlotOverrides) || {};

  const sortedSlots = useMemo(
    () => getTimeSlotsForDate(config, selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null, courts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.timeSlots, config?.timeSlotSets, config?.courts, selectedDate, courts.join(',')],
  );
  // Every cell a promo covers, as "hour|court" keys — built once per render
  // rather than asked per cell, since each question walks the promo list.
  // Mirrors the guest grid so the two never disagree about which hours are
  // on offer.
  const promoCells = useMemo(() => {
    const marked = new Set();
    // Keyed by the ROW's date — a promo running tonight need not run into
    // tomorrow morning.
    gridRows.forEach(({ slot: timeSlot, dateStr }) => {
      courts.forEach((court) => {
        if (hasActivePromo(config, { court, timeSlot, sportId: activeSportId || null, dateStr })) {
          marked.add(`${dateStr}|${timeSlot}|${court}`);
        }
      });
    });
    return marked;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.promos, config?.courts, selectedDate, activeSportId, courts.join(','), gridRows]);

  // { [court]: Set(hours it is open) }, resolved once per render — this grid
  // draws every hour against every court.
  const courtSlots = useMemo(() => {
    const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
    return Object.fromEntries(courts.map(court => [
      court,
      new Set(getTimeSlotsForCourt(config, dateStr, court)),
    ]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.timeSlots, config?.timeSlotSets, selectedDate, courts.join(',')]);
  // Drives the scrollbar rail rendered above the grid. Remeasures when the
  // number of columns or rows changes; a ResizeObserver covers the rest.
  // Declared here, after `courts` and `sortedSlots` — it reads both.
  const { trackRef, thumbRef, scrollerRef } = useTopScrollbar([courts.length, gridRows.length]);
  // Where each block booking sits on this grid (Config -> Booking Settings).
  // Whether a block is actually drawn merged is decided at render time —
  // see mergedBlocks — since that depends on the cells' current statuses.
  const blockSpans = useMemo(
    () => buildBlockSpans(config, gridRows, courts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.bookingSettings, config?.timeSlots, config?.timeSlotSets, gridRows, courts.join(',')],
  );
  // A separator row wherever the hourly rate changes, same as the guest grid,
  // and the grid line each hour row lands on once those rows are slotted in.
  // Switched off (Config -> Sports & Courts -> Hourly rates), nothing is
  // planned and every row keeps its plain line.
  const showSeparators = isRateSeparatorShown(config);
  const separatorDesign = resolveRateSeparatorDesign(config);
  const rateSeparators = useMemo(
    () => planRateSeparators(
      gridRows,
      showSeparators
        ? row => getRateBandForSlot(config, row.slot, activeSportId || null, row.dateStr)
        : () => null,
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.sports, config?.priceOffPeak, config?.pricePeak, config?.peakRateStart, activeSportId, gridRows, showSeparators],
  );
  const rowLines = rateSeparators.rowLines;
  // Matched pill widths, and the time column's width so each pill centres
  // over the court cells only (RateSeparatorRow.css reads both).
  useMatchedWidths(scrollerRef, '.rsr-row .rsp-pill', '--rsr-pill-w', [rateSeparators, config, courts.join(',')]);
  useMatchedWidths(scrollerRef, '.ats-time-cell', '--rsr-lead', [rateSeparators, gridRows, courts.join(',')]);
  // { [court]: boolean } — resolved once per render rather than per cell,
  // since this grid draws ~17 rows × every court.
  const courtAvailability = useMemo(
    () => Object.fromEntries(courts.map(c => [c, isCourtAvailable(config, c)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, courts.join(',')]
  );

  /**
   * The booking sitting on one cell, read from that cell's OWN date.
   * @param {string} rowDate
   * @param {string} timeSlot
   * @param {number} court
   * @returns {object|null}
   */
  const getBookingForCourt = (rowDate, timeSlot, court) => (
    (bookedSlotsFor(rowDate)[timeSlot] || []).find(b => b.court === court) || null
  );

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const selectedStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
  const isViewingToday = selectedStr === todayStr;
  const isViewingPast = !!selectedStr && selectedStr < todayStr;
  // Config -> System -> "View past dates". When off, the date bar stops at
  // today and a past date that is already selected snaps back to today.
  const allowPastDates = isPastDateViewEnabled(config);
  // Config -> System -> "Availability status options".
  const enabledStatuses = useMemo(() => getEnabledGridStatuses(config), [config]);
  // Config -> System -> "Ask reason for closing timeslot".
  const askCloseReason = isCloseReasonPromptEnabled(config);
  useEffect(() => {
    if (!allowPastDates && isViewingPast) onDateSelect(today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowPastDates, isViewingPast]);
  /**
   * Whether a grid row belongs to a date that has already gone. Asked per
   * ROW: an after-midnight row under yesterday's date is today, not the past.
   * @param {string} rowDate - 'yyyy-MM-dd'
   * @returns {boolean}
   */
  const isPastRow = (rowDate) => !!rowDate && rowDate < todayStr;
  const currentHour = new Date().getHours();

  // Pending reservations for the currently selected date only — drives the
  // Reservation Queue's "Current Date" tab.
  // `pendingQueue` entries are grouped (see groupBookingRecords) — a
  // multi-date booking's `dates` array is checked for ANY date matching
  // the one currently selected, not just a single top-level `date` field.
  const pendingForSelectedDate = pendingQueue.filter(b => (b.dates || []).some(d => d.date === selectedStr));

  // CONFIRMED reservations for the selected date — what the Booking
  // Summary's "Copy Details" exports. (It used to copy the pending queue,
  // which is the review backlog, not the day's actual schedule.)
  //
  // Rebuilt from `bookedSlots` rather than a second Firebase query: that
  // subscription already streams every non-cancelled record for this date
  // with its full payload attached, so deriving from it costs no extra
  // download. bookedSlots repeats a booking once per (time slot, court),
  // hence the de-duplication by booking id.
  //
  // No group stitching here, unlike pendingQueue: a multi-date booking is
  // stored as one record per date, and one date's record is exactly what a
  // single day's summary should list.
  const confirmedForSelectedDate = useMemo(() => {
    const byId = new Map();
    Object.values(bookedSlots || {}).forEach((entries) => {
      (entries || []).forEach((entry) => {
        if (entry?.status !== 'confirmed' || !entry.bookingId) return;
        if (!byId.has(entry.bookingId)) {
          byId.set(entry.bookingId, { id: entry.bookingId, ...(entry.originalData || {}) });
        }
      });
    });
    // Chronological, so the copied block reads like the day's run sheet
    // rather than in whatever order Firebase happened to return records.
    return [...byId.values()].sort((a, b) => firstSlotHour(a) - firstSlotHour(b));
  }, [bookedSlots]);

  // Open Play is hosted per SESSION with its players attached as separate
  // join records, so it can't come off `bookedSlots` like the court bookings
  // above — it needs its own listener, re-pointed whenever the admin moves
  // to another day.
  useEffect(() => {
    if (!selectedStr) {
      setOpenPlayDay([]);
      return undefined;
    }
    const unsubscribe = subscribeOpenPlayDay(selectedStr, setOpenPlayDay);
    return () => unsubscribe();
  }, [selectedStr]);

  // Sessions with at least one CONFIRMED player. subscribeOpenPlayDay also
  // hands back joins still awaiting approval; those belong in the queue, not
  // in a summary of what's settled — the same rule the court list applies.
  const confirmedOpenPlayForSelectedDate = useMemo(() => (
    (openPlayDay || [])
      .map(session => ({
        ...session,
        joins: (session.joins || []).filter(join => join.status === 'confirmed'),
      }))
      .filter(session => session.joins.length > 0)
  ), [openPlayDay]);

  // Everything the copied block will contain. Each Open Play join is one
  // guest who paid, so it counts the same as a court reservation here.
  const confirmedOpenPlayJoinCount = confirmedOpenPlayForSelectedDate
    .reduce((total, session) => total + session.joins.length, 0);
  const summaryCount = confirmedForSelectedDate.length + confirmedOpenPlayJoinCount;

  // Reservation Queue list respects the Current Date / All Dates tab.
  const queueFiltered = queueDateFilter === 'current' ? pendingForSelectedDate : pendingQueue;

  // Pagination (5 per page). Clamp back to the last valid page if items
  // got confirmed/declined out from under the admin, or reset to page 1
  // whenever the tab or selected date changes.
  const queueTotalPages = Math.max(1, Math.ceil(queueFiltered.length / QUEUE_PAGE_SIZE));
  useEffect(() => {
    setQueuePage(p => Math.min(p, queueTotalPages));
  }, [queueTotalPages]);
  useEffect(() => {
    setQueuePage(1);
  }, [queueDateFilter, selectedStr]);
  const pagedQueue = queueFiltered.slice((queuePage - 1) * QUEUE_PAGE_SIZE, queuePage * QUEUE_PAGE_SIZE);

  const computeOriginalStatus = (rowDate, timeSlot, court, booking) => {
    if (booking) {
      if (booking.status !== 'confirmed') return 'processing';
      // Confirmed, but the guest still owes the balance — worth saying on
      // the cell itself, since this is the screen staff are on when that
      // guest walks up to the counter.
      return getBookingPaymentState(booking.originalData || booking).isPartial
        ? GRID_STATUS_PARTIAL
        : 'booked';
    }
    // A manual override (set by the admin via this same grid) takes
    // precedence over the automatic time-based "passed" check, since it
    // reflects the admin's explicit, most recent choice for this cell.
    const override = overridesFor(rowDate)?.[timeSlot]?.[court];
    if (override) return override;
    // Asked of the ROW's date, not the viewed one: an after-midnight row is
    // tomorrow, so nothing on it has passed while today is still running.
    if (rowDate && todayStr) {
      if (rowDate < todayStr) return 'passed';
      if (rowDate === todayStr && isSlotPassed(true, timeSlot, currentHour)) return 'passed';
    }
    return 'open';
  };

  /**
   * The saved close reason for a cell, or the one staged in this edit.
   * @returns {string} '' when the hour has no recorded reason
   */
  const closeReasonFor = (rowDate, timeSlot, court) => {
    const staged = pendingChanges[cellKey(rowDate, timeSlot, court)];
    if (staged?.newStatus === SLOT_STATUS_CLOSED) return staged.closeReason || '';
    const reasons = rowDate === viewedDateStr ? slotCloseReasons : nextDaySlotCloseReasons;
    return getCloseReason(reasons, timeSlot, court);
  };

  /**
   * The grid dropdown's change handler. Picking Closed on a cell that isn't
   * already closed opens the reason prompt instead of staging straight away
   * (unless Config -> System -> "Ask reason for closing timeslot" is off, in
   * which case it stages with no reason); the select is controlled, so it
   * keeps showing the old status until the reason is confirmed (and simply
   * stays put if the prompt is cancelled).
   * @returns {void}
   */
  const handleStatusSelect = (rowDate, timeSlot, court, booking, newStatus) => {
    const original = computeOriginalStatus(rowDate, timeSlot, court, booking);
    if (askCloseReason && newStatus === SLOT_STATUS_CLOSED && original !== SLOT_STATUS_CLOSED) {
      setClosePrompt({ rowDate, timeSlot, court, booking });
      return;
    }
    cellHours(rowDate, timeSlot, court).forEach((slot) => {
      handleStatusChange(rowDate, slot, court, getBookingForCourt(rowDate, slot, court), newStatus);
    });
  };

  /**
   * The hours one grid control acts on: every hour of a merged block cell
   * (Config -> Booking Settings), or just the one hour otherwise. A block
   * left unmerged because its hours disagree is edited hour by hour, which
   * is the only way to put it right.
   *
   * @param {string} rowDate
   * @param {string} timeSlot
   * @param {number} court
   * @returns {string[]}
   */
  const cellHours = (rowDate, timeSlot, court) => {
    for (const [startKey, span] of mergedBlocks.starts) {
      if (Number(startKey.split('|')[1]) === Number(court) && span.dateStr === rowDate && span.slots.includes(timeSlot)) {
        return span.slots;
      }
    }
    return [timeSlot];
  };

  /**
   * Stages the Closed status with the reason typed into the prompt.
   * @param {string} reason - trimmed, non-empty
   * @returns {void}
   */
  const handleCloseReasonConfirm = (reason) => {
    if (!closePrompt) return;
    const { rowDate, timeSlot, court } = closePrompt;
    cellHours(rowDate, timeSlot, court).forEach((slot) => {
      handleStatusChange(rowDate, slot, court, getBookingForCourt(rowDate, slot, court), SLOT_STATUS_CLOSED, reason);
    });
    setClosePrompt(null);
  };

  const handleStatusChange = (rowDate, timeSlot, court, booking, newStatus, closeReason = '') => {
    const key = cellKey(rowDate, timeSlot, court);
    const original = computeOriginalStatus(rowDate, timeSlot, court, booking);
    setPendingChanges(prev => {
      const next = { ...prev };
      if (newStatus === original) {
        delete next[key];
      } else {
        // dateStr travels with the change so the save writes each cell
        // against its own date (see handleSaveGridChanges). sportId travels
        // with it for the same reason the date does: a rate is looked up per
        // sport, and the save has no other way to know which tab it came
        // from.
        next[key] = { dateStr: rowDate, timeSlot, court, booking, originalStatus: original, newStatus, sportId: activeSportId || null };
        // Saved to slotCloseReasons and the audit entry by handleSaveGridChanges.
        if (newStatus === SLOT_STATUS_CLOSED) next[key].closeReason = closeReason;
        // Initials already typed into this cell survive a dropdown press
        // that keeps the hour BOOKED — re-picking the status the badge just
        // set should not quietly throw the name away. Any other status means
        // the hour is no longer a walk-in's, so they go.
        if (newStatus === 'booked' && prev[key]?.adminInitials) {
          next[key].adminInitials = prev[key].adminInitials;
          next[key].bookedByInitials = prev[key].bookedByInitials === true;
        }
      }
      return next;
    });
  };

  /**
   * Stages the initials typed into an open cell's badge, flipping the hour to
   * BOOKED in the same edit.
   *
   * Two letters in the box and BOOKED in the dropdown are one decision for
   * the admin — an hour just went to a walk-in — so they are staged as one
   * pendingChanges entry and land in one Save changes. Clearing the box is
   * the same decision in reverse and hands the hour back to OPEN.
   *
   * The initials ride on the change as `adminInitials`; handleSaveGridChanges
   * passes them to createAdminBlockBooking as the booker name, so the badge
   * reads back the same two letters after the save.
   *
   * @param {string} rowDate - 'yyyy-MM-dd' for the row (not always the viewed date).
   * @param {string} timeSlot
   * @param {number} court
   * @param {object|null} booking - Any booking already on the cell.
   * @param {string} initials - Sanitized initials, '' to clear.
   * @returns {void}
   */
  const handleInitialsCommit = (rowDate, timeSlot, court, booking, initials) => {
    // A merged block cell books (or frees) every hour of the block at once;
    // all of them land on one reservation when saved (handleSaveGridChanges).
    cellHours(rowDate, timeSlot, court).forEach((slot) => {
      stageInitialsForHour(rowDate, slot, court, slot === timeSlot ? booking : getBookingForCourt(rowDate, slot, court), initials);
    });
  };

  /**
   * handleInitialsCommit for ONE hour — see the description above it.
   * @returns {void}
   */
  const stageInitialsForHour = (rowDate, timeSlot, court, booking, initials) => {
    const key = cellKey(rowDate, timeSlot, court);
    const original = computeOriginalStatus(rowDate, timeSlot, court, booking);
    setPendingChanges(prev => {
      const next = { ...prev };
      const existing = prev[key];
      if (!initials) {
        // The box was emptied. Who booked the hour decides what that means:
        // if typing the initials is what booked it, taking them away hands
        // the hour back. If the admin had already set BOOKED on the dropdown,
        // that is their decision and it stands — only the name goes.
        if (existing && !existing.bookedByInitials) {
          const { adminInitials, ...withoutInitials } = existing;
          next[key] = withoutInitials;
          return next;
        }
        // Back to what the hour was before this edit. If that is what it
        // already is, the cell has no change on it at all and must not sit
        // in the unsaved count.
        if (original === 'open') {
          delete next[key];
        } else {
          next[key] = { dateStr: rowDate, timeSlot, court, booking, originalStatus: original, newStatus: 'open', sportId: activeSportId || null };
        }
        return next;
      }
      next[key] = {
        dateStr: rowDate,
        timeSlot,
        court,
        booking,
        originalStatus: original,
        newStatus: 'booked',
        adminInitials: initials,
        // Records which control booked the hour, for the emptied-box case
        // above. Typing into a cell the dropdown had ALREADY set to BOOKED
        // leaves that flag as it was, so the dropdown keeps its say.
        bookedByInitials: existing?.newStatus === 'booked'
          ? existing.bookedByInitials === true
          : true,
        sportId: activeSportId || null,
      };
      return next;
    });
  };

  // "Book New Slot" (AdminBooking.jsx's modal) stages the booking here
  // rather than writing it straight to Firebase, so a new reservation
  // follows the same Save changes / Clear changes flow as every other grid
  // edit. Exposed imperatively because the modal lives in the parent while
  // the staging state lives here — see the gridRef in AdminBooking.jsx.
  const stageNewBooking = (timeSlot, court, details, rowDate = viewedDateStr) => {
    // A merged block cell stages the booking on every hour of the block.
    // The save groups staged cells by guest, so they still become ONE
    // reservation rather than one per hour.
    cellHours(rowDate, timeSlot, court).forEach((slot) => {
      const key = cellKey(rowDate, slot, court);
      const original = computeOriginalStatus(rowDate, slot, court, null);
      setPendingChanges(prev => ({
        ...prev,
        [key]: {
          dateStr: rowDate,
          timeSlot: slot,
          court,
          booking: null,
          originalStatus: original,
          newStatus: 'booked',
          newBooking: details,
          sportId: activeSportId || null,
        },
      }));
    });
  };
  /**
   * Stages the release of ONE booked cell, for the Manage Booking modal's
   * cancel button.
   *
   * The modal lives in the parent while the staging state lives here, so it
   * reaches this the same imperative way "Book New Slot" does. It stages
   * exactly what the cell's dropdown stages for OPEN, so one save path
   * handles both and nothing reaches Firebase until Save changes.
   *
   * @param {string} rowDate - 'yyyy-MM-dd' for the row the cell is on.
   * @param {string} timeSlot
   * @param {number} court
   * @param {object} booking - The booking currently on the cell.
   * @returns {void}
   */
  const stageCellRelease = (rowDate, timeSlot, court, booking) => {
    // A merged block is released whole, the same way it was booked.
    cellHours(rowDate, timeSlot, court).forEach((slot) => {
      handleStatusChange(rowDate, slot, court, slot === timeSlot ? booking : getBookingForCourt(rowDate, slot, court), 'open');
    });
  };
  useImperativeHandle(ref, () => ({ stageNewBooking, stageCellRelease }));

  const changeList = Object.values(pendingChanges);
  const changeCount = changeList.length;

  // Discards every staged edit and snaps the grid back to what's actually
  // in Firebase — each cell falls back to computeOriginalStatus once its
  // pendingChanges entry is gone, so no refetch is needed.
  const handleClearChanges = () => setPendingChanges({});

  const handleSave = async () => {
    if (changeCount === 0) return;
    // Hours being taken off existing reservations. Counted in HOURS, not
    // bookings: releasing one hour of a three-hour reservation now trims that
    // booking rather than cancelling it (see handleSaveGridChanges), so
    // warning about bookings cancelled would overstate what is about to
    // happen. A reservation is only cancelled outright when its last hour
    // goes, which the wording below leaves room for.
    const willRelease = changeList.filter(c =>
      GRID_BOOKED_CELL_STATUSES.includes(c.originalStatus) &&
      c.newStatus !== c.originalStatus
    );
    if (willRelease.length > 0) {
      const ok = window.confirm(
        `${willRelease.length} booked hour${willRelease.length > 1 ? 's' : ''} will be freed by this change, and any reservation left with no hours will be cancelled. Continue?`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await onSaveChanges(changeList, selectedDate);
      setPendingChanges({});
      setSaveMessage(`${changeCount} change${changeCount > 1 ? 's' : ''} saved.`);
    } catch (error) {
      console.error('Error saving grid changes:', error);
      setSaveMessage('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * The saved status of one cell as the availability images should show it.
   * Reuses the grid's own rules (computeOriginalStatus, the court/hour locks
   * and promo marks) so an image can never disagree with this screen. Staged,
   * unsaved edits are deliberately ignored — the image should only advertise
   * what guests can actually see.
   * @param {string} rowDate - 'yyyy-MM-dd' of the row (the next date for after-midnight rows)
   * @param {string} timeSlot
   * @param {number} court
   * @returns {{status: string, locked: boolean, promo: boolean, passed: boolean}}
   */
  const resolveImageCell = (rowDate, timeSlot, court) => {
    const isNextDayRow = rowDate !== viewedDateStr;
    const locked = !courtAvailability[court]
      || !(isNextDayRow ? nextDayCourtSlots : courtSlots)[court]?.has(timeSlot);
    const booking = getBookingForCourt(rowDate, timeSlot, court);
    return {
      status: computeOriginalStatus(rowDate, timeSlot, court, booking),
      locked,
      promo: promoCells.has(`${rowDate}|${timeSlot}|${court}`),
      // By the clock, the same check computeOriginalStatus uses — a booked
      // or overridden hour keeps its own status after it ends, but it has
      // still passed and is left off the image.
      passed: Boolean(rowDate && todayStr) && (
        rowDate < todayStr || (rowDate === todayStr && isSlotPassed(true, timeSlot, currentHour))
      ),
    };
  };

  /**
   * Freezes the selected date's availability (the courts on screen — the
   * active sport's, for a multi-sport club) and opens the image dialog.
   * @returns {void}
   */
  const handleOpenAvailabilityImages = () => {
    if (!viewedDateStr) return;
    const sport = (config?.sports || []).find(s => s.id === activeSportId);
    try {
      setAvailabilitySnapshot(buildAvailabilitySnapshot({
        config,
        dateStr: viewedDateStr,
        rows: gridRows,
        courts,
        resolveCell: resolveImageCell,
        sportName: sport?.name || '',
        siteHost: window.location.host,
      }));
    } catch (error) {
      console.error('Could not prepare availability images:', error);
      setSaveMessage('Could not prepare the availability images. Please try again.');
    }
  };

  /**
   * The image dialog's loader for any date other than the one on screen, or
   * a range of dates: one read of bookings and overrides across the range,
   * then a snapshot per date built with the same rules as this grid's
   * (see availabilityRangeUtil.js). Scoped to the courts on screen.
   * @param {string} startStr - 'yyyy-MM-dd'
   * @param {string} endStr - 'yyyy-MM-dd'
   * @returns {Promise<object[]>}
   */
  const loadAvailabilityRange = (startStr, endStr) => {
    const sport = (config?.sports || []).find(s => s.id === activeSportId);
    return loadAvailabilitySnapshotsForRange({
      config,
      startStr,
      endStr,
      courts,
      sportId: activeSportId || null,
      sportName: sport?.name || '',
      siteHost: window.location.host,
    });
  };

  // Copies the CONFIRMED reservations for the selected date, formatted as
  // one reference block per booking (date header, then each booking's
  // reference number / name / contact / court(s) / schedule / equipment,
  // "-------" separated), followed by a block per Open Play session with its
  // confirmed players — the day's settled schedule, which is what this gets
  // pasted into a group chat or handover note for.
  //
  // Everything it reads is already in state (bookedSlots + the Open Play
  // listener), so the whole block is built synchronously and the clipboard
  // write stays inside the click's user gesture — Safari rejects a
  // writeText() that happens after an await on a fetch.
  const handleCopyDetails = async () => {
    const header = selectedDate ? format(selectedDate, 'MMMM d, yyyy') : '';
    const lines = [header, '-------'];
    confirmedForSelectedDate.forEach(booking => {
      lines.push(`Reference number: ${booking.confirmationId || booking.id}`);
      lines.push(`Name: ${booking.userName || ''}`);
      lines.push(`Contact number: ${booking.phone || ''}`);
      // Named so the block says what the guest bought, since a package's
      // one-price block reads nothing like an hourly reservation.
      if (isPackageBooking(booking)) lines.push(`Package: ${booking.packageName || 'Package'}`);
      lines.push(`Court/s Reserved: ${formatBookingCourts(booking, config?.numberOfCourts, config)}`);
      lines.push(`Schedule: ${formatBookingSchedule(booking)}`);
      // Only when something was actually rented — an "Equipment rental:
      // none" line on every block is noise in a chat handover.
      const equipment = formatBookingEquipment(booking);
      if (equipment) lines.push(`Equipment rental: ${equipment}`);
      lines.push('-------');
    });

    // Open Play is one shared session with many paying guests rather than a
    // reservation per guest, so it's listed as a session block with its
    // roster underneath instead of being forced into the shape above.
    confirmedOpenPlayForSelectedDate.forEach(session => {
      lines.push(`Open Play: ${session.sportName || 'Open Play'}`);
      lines.push(`Court/s Reserved: ${formatCourtNumbers(getSessionCourtNumbers(session), config?.numberOfCourts, config)}`);
      lines.push(`Schedule: ${formatSessionSchedule(session)}`);
      lines.push('Players:');
      session.joins.forEach(join => {
        const players = Number(join.numPlayers) || 1;
        const parts = [`${join.fullName || 'Guest'} (${players} player${players === 1 ? '' : 's'})`];
        if (join.phone) parts.push(join.phone);
        parts.push(join.confirmationId || join.id);
        lines.push(`- ${parts.join(' / ')}`);
      });
      lines.push('-------');
    });

    if (summaryCount === 0) lines.push('No confirmed reservations for this day.');
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage(`Copied ${summaryCount} confirmed reservation${summaryCount === 1 ? '' : 's'} for ${header}.`);
    } catch (error) {
      console.error('Error copying details:', error);
      setCopyMessage('Could not copy to clipboard.');
    }
  };

  const handleJumpChange = (e) => {
    if (!e.target.value) return;
    const [y, m, d] = e.target.value.split('-').map(Number);
    const picked = new Date(y, m - 1, d);
    // Refused outright when past dates are switched off in Config -> System;
    // the input's `min` already stops the picker, this covers typed dates.
    if (!allowPastDates && dateKey(picked) < dateKey(today)) return;
    // Past dates are allowed (when switched on in Config -> System) and
    // editable exactly like any other date.
    onDateSelect(picked);
  };
  // Same arrangement as the guest booking screen: the date input itself is
  // the tap target, layered transparently over the styled pill (see
  // .ats-jumpdate). iOS Safari only opens a date picker when the user taps
  // the input directly — a programmatic showPicker()/click() fired from a
  // separate button does nothing there, which is why this button was dead on
  // iPhone while working on Android and Windows.
  //
  // showPicker() still runs for desktop, where clicking the text part of a
  // date field only focuses a segment instead of opening the calendar.
  const openJumpPicker = () => {
    const el = jumpInputRef.current;
    if (!el || typeof el.showPicker !== 'function') return;
    try {
      el.showPicker();
    } catch {
      // Ignored — browsers that reject the call (iOS) have already opened
      // their own picker from the tap.
    }
  };

  const winFirst = addDays(today, winStart);
  const winLast = addDays(today, winStart + WINDOW - 1);
  const winLabel = winFirst.getMonth() === winLast.getMonth()
    ? `${MON[winFirst.getMonth()]} ${winFirst.getFullYear()}`
    : `${MON_S[winFirst.getMonth()]} – ${MON_S[winLast.getMonth()]} ${winLast.getFullYear()}`;

  /**
   * Everything one admin grid cell needs: its saved and staged status, the
   * booking on it, which controls it gets and why it may be locked. Pulled
   * out of the render so a block booking can resolve each of its hours the
   * same way before deciding whether they can share one merged cell.
   *
   * @param {{slot: string, dateStr: string, dayOffset: number}} row
   * @param {number} court
   * @returns {object}
   */
  const resolveCell = (row, court) => {
    const timeSlot = row.slot;
    const rowDate = row.dateStr;
    const isNextDay = row.dayOffset === 1;
    const booking = getBookingForCourt(rowDate, timeSlot, court);
    const original = computeOriginalStatus(rowDate, timeSlot, court, booking);
    const key = cellKey(rowDate, timeSlot, court);
    const current = pendingChanges[key]?.newStatus ?? original;
    // A cell already persisted as Open Play is only
    // editable via its session's card below the grid now
    // (see the STATUS_OPTIONS comment above) — no
    // dropdown, no "manage booking" button, just a locked
    // badge. `original` (not `current`) is checked since
    // the dropdown that could ever set `current` away
    // from an openPlay original no longer exists.
    const isLockedOpenPlay = original === 'openPlay';
    // Guest details captured by "Book New Slot" but not
    // written to Firebase yet (see stageNewBooking).
    const stagedBooking = pendingChanges[key]?.newBooking;
    // Initials typed straight into the badge and not saved
    // yet (see handleInitialsCommit).
    const stagedInitials = pendingChanges[key]?.adminInitials || '';
    // The badge is typeable on any hour that has no guest
    // behind it yet — free, or marked BOOKED in this save
    // and not yet written. A cell that already carries a
    // booking (real or staged from the full form) keeps the
    // button, because renaming that guest two letters at a
    // time is the booking modal's job.
    //
    // BOOKED is included whichever way the cell got there.
    // Reaching it from the dropdown used to leave a button
    // that opened the modal, so the one thing an admin
    // obviously wants next — to say whose hour it is —
    // needed the long form. It also keeps the field up
    // after typing flips the cell to BOOKED, so a typo is
    // fixable without first putting the hour back to OPEN.
    // On a past row every free hour reads the automatic
    // 'passed', so that counts as free too — otherwise a
    // walk-in could never be typed onto a finished day.
    const pastRow = isPastRow(rowDate);
    const canTypeInitials = !booking && !stagedBooking
      && (current === 'open' || current === 'booked' || (pastRow && current === 'passed'));
    const baseOptions = isAddOnCourt(config, court) ? ADDON_STATUS_OPTIONS : STATUS_OPTIONS;
    // Only the statuses ticked in Config -> System, plus
    // whatever the cell already shows (saved or staged) —
    // a controlled <select> whose value has no matching
    // option would display the wrong status.
    const cellOptions = baseOptions.filter(opt => enabledStatuses.has(opt.value)
      || opt.value === original
      || opt.value === current);

    // Whole court switched off in Config -> Sports &
    // Courts. Every cell on it locks — no dropdown, no
    // manage button — matching how the guest grid treats
    // a closed court. To manage a reservation still
    // sitting on one, switch the court back on first.
    const courtClosed = !courtAvailability[court];
    // Tinted only on a cell that is otherwise plain OPEN.
    // A booked, processing, passed or closed cell already
    // uses its background to say something the admin needs
    // more than the promo flag, and overwriting that would
    // trade a fact for a hint.
    const onPromo = current === 'open' && promoCells.has(`${rowDate}|${timeSlot}|${court}`);
    // The row exists because another court is open this
    // hour, but a court-scoped time-slot set leaves this one
    // shut. Locked like a switched-off court so the admin
    // can't book or set a status on an hour the club does
    // not open — the guest grid shows the same cell CLOSED.
    // Court hours belong to the row's date: the scoping
    // that closes a court at 2PM applies at 2AM too, and an
    // after-midnight row must ask the NEXT day's sets.
    const outOfHours = !(isNextDay ? nextDayCourtSlots : courtSlots)[court]?.has(timeSlot);
    const lockClosedCell = courtClosed || outOfHours;
    // Reads as "not partial" for a free cell and for every
    // booking made before downpayments existed.
    const cellPayment = getBookingPaymentState(booking?.originalData || booking);

    // Hovering a CLOSED hour shows why it was closed.
    const closeReason = current === SLOT_STATUS_CLOSED && !lockClosedCell
      ? closeReasonFor(rowDate, timeSlot, court)
      : '';

    return {
      rowDate, timeSlot, booking, original, current, isLockedOpenPlay, stagedBooking, stagedInitials,
      canTypeInitials, cellOptions, courtClosed, onPromo, lockClosedCell, cellPayment, closeReason,
    };
  };

  /**
   * Draws one cell of the grid — or, when `blockSpan` is given, one merged
   * cell standing for every hour of a block booking. The controls are the
   * same either way: the handlers they call expand a block cell to all of
   * its hours (see blockSlotsFor).
   *
   * @param {object} cell - from resolveCell (the block's first hour, for a merged cell)
   * @param {number} court
   * @param {object} placement - explicit grid-row / grid-column style
   * @param {object|null} [blockSpan] - from buildBlockSpans
   * @returns {JSX.Element}
   */
  const renderCell = (cell, court, placement, blockSpan = null) => {
    const {
      rowDate, timeSlot, booking, current, isLockedOpenPlay, stagedBooking, stagedInitials,
      canTypeInitials, cellOptions, courtClosed, onPromo, lockClosedCell, cellPayment, closeReason,
    } = cell;
    return (
      <div
        key={court}
        className={`ats-cell ats-status-${current}${onPromo ? ' ats-promo' : ''}${courtClosed ? ' ats-court-closed' : ''}${blockSpan ? ' ats-cell-block' : ''}`}
        style={placement}
        title={current === SLOT_STATUS_CLOSED && !lockClosedCell
          ? `Closed — ${closeReason || 'no reason recorded'}`
          : undefined}
      >
        {/* A block booking (Config -> Booking Settings) drawn as one cell:
            every control below acts on all of its hours at once. */}
        {blockSpan && (
          <span className="ats-block-tag" title={describeBlockRange(blockSpan.block)}>
            {blockSpan.block.label || 'Block'} {'\u00b7'} {blockSpan.slots.length} hrs
          </span>
        )}
        {isLockedOpenPlay ? (
          <div
            className="ats-cell-openplay-locked"
            title="Managed via its Open Play session card below — click that card to edit or remove it."
          >
            <span className="ats-op-badge">OP</span>
            <span className="ats-op-locked-label">Open Play</span>
          </div>
        ) : lockClosedCell ? (
          <div
            className="ats-cell-closed-locked"
            title={courtClosed
              ? 'This court is switched off in Config → Sports & Courts. Turn it back on to take bookings.'
              : 'This court is not open at this hour — see Config → Sports & Courts → Time Slots.'}
          >
            <span className="ats-closed-label">{courtClosed ? 'Unavailable' : 'Closed'}</span>
          </div>
        ) : (
          <>
            {canTypeInitials ? (
              <CellInitialsInput
                value={stagedInitials}
                onCommit={(initials) => handleInitialsCommit(rowDate, timeSlot, court, booking, initials)}
                onOpenModal={() => onManageBooking(rowDate, timeSlot, court, booking)}
                title="Type initials to book this hour, or double-click for the full booking form"
              />
            ) : (
            <button
              type="button"
              className="ats-initials-btn"
              onClick={() => onManageBooking(rowDate, timeSlot, court, booking)}
              title={
                closeReason
                  ? `Closed — ${closeReason}`
                  : stagedBooking
                  ? `${stagedBooking.fullName} — unsaved, press Save changes to book`
                  : booking
                    ? `${booking.userName} — ${booking.status}${cellPayment.isPartial
                      ? ` · partially paid, ${formatMoney(cellPayment.balanceDue, config)} still due`
                      : ''}`
                    : 'No booking — click to add one'
              }
            >
              {/* A staged (not yet saved) booking shows its
                  guest's initials too, so the cell looks
                  the same as a real one before saving. */}
              {getInitials(stagedBooking ? { userName: stagedBooking.fullName } : booking)}
            </button>
            )}
            {/* The admin can set any enabled status regardless
                of the cell's current one, on past dates too. */}
            <select
              className="ats-status-select"
              value={current}
              disabled={cellOptions.length < 2}
              onChange={(e) => handleStatusSelect(rowDate, timeSlot, court, booking, e.target.value)}
            >
              {cellOptions.map(opt => (
                // A non-selectable option ('Partial') still
                // renders, so a cell in that state can show
                // it — it just cannot be chosen here.
                <option key={opt.value} value={opt.value} disabled={opt.selectable === false}>
                  {opt.label}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
    );
  };

  // Block bookings (Config -> Booking Settings) that can be drawn as ONE
  // cell. Only a block whose hours all read the same — same status, same
  // booking, nothing locked — is merged: a block an earlier edit left mixed
  // (one hour closed, another booked) falls back to separate cells, so the
  // admin can still see and fix each hour.
  const mergedBlocks = (() => {
    const starts = new Map();
    const covered = new Set();
    blockSpans.starts.forEach((span, startKey) => {
      const [rowIndexText, courtText] = startKey.split('|');
      const rowIndex = Number(rowIndexText);
      const court = Number(courtText);
      const cells = gridRows.slice(rowIndex, rowIndex + span.rowSpan).map(r => resolveCell(r, court));
      const first = cells[0];
      const uniform = cells.every(c => !c.isLockedOpenPlay && !c.lockClosedCell
        && c.current === first.current
        && (c.booking?.bookingId || null) === (first.booking?.bookingId || null)
        && !!c.stagedBooking === !!first.stagedBooking);
      if (!uniform) return;
      starts.set(startKey, span);
      for (let k = 1; k < span.rowSpan; k += 1) covered.add(`${rowIndex + k}|${court}`);
    });
    return { starts, covered };
  })();

  return (
    <div className="ats-page">
      <div className="ats-heading">
        <h1>Manage availability</h1>
        <p>Approve reservations with a single tap in the Reservation Queue, set each hour's availability status, then Save. Customers see these updates live.</p>
      </div>

      {/* Shared Ownership left half-configured means courts silently stop
          being bookable — guests just get a "contact us" message, which the
          club never sees. Surfaced here because this is the screen an admin
          has open while wondering why nothing is coming in. Renders nothing
          unless the feature is on AND something is actually wrong. */}
      <OwnershipWarningBanner config={config} />

      <div className="ats-layout">
        <div className="ats-main">
          <div className="ats-card">
            {/* ---- Date bar (same pattern as the guest booking screen) ---- */}
            <div className="ats-datebar">
              <div className="ats-daterow">
                <button
                  type="button"
                  className="ats-arrow"
                  aria-label="Previous 14 days"
                  // The window starts at today (winStart 0), so paging back
                  // from there only reaches past dates.
                  disabled={!allowPastDates && winStart <= 0}
                  onClick={() => setWinStart(w => (allowPastDates ? w - WINDOW : Math.max(0, w - WINDOW)))}
                >&lsaquo;</button>

                <div className="ats-curdate">
                  <div className="ats-dow">{selectedDate ? (isViewingToday ? 'Today' : DOW[selectedDate.getDay()]) : '—'}</div>
                  <div className="ats-full">{selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : '—'}</div>
                </div>

                {/* Presentational pill + the transparent date input that is
                    the real control — see openJumpPicker above. */}
                <span className="ats-jumpwrap">
                  <span className="ats-jumpbtn" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    <span className="ats-jl">Jump to date</span>
                  </span>
                  <input
                    ref={jumpInputRef}
                    type="date"
                    className="ats-jumpdate"
                    value={selectedDate ? dateKey(selectedDate) : ''}
                    min={allowPastDates ? undefined : dateKey(today)}
                    onChange={handleJumpChange}
                    onClick={openJumpPicker}
                    aria-label="Jump to date"
                  />
                </span>

                <button
                  type="button"
                  className="ats-arrow"
                  aria-label="Next 14 days"
                  onClick={() => setWinStart(w => w + WINDOW)}
                >&rsaquo;</button>
              </div>

              <div className="ats-winlabel">{winLabel.toUpperCase()}</div>

              <div className="ats-chips">
                {Array.from({ length: WINDOW }, (_, i) => {
                  const d = addDays(today, winStart + i);
                  const active = sameYMD(d, selectedDate);
                  const isToday = sameYMD(d, today);
                  return (
                    <button
                      type="button"
                      key={dateKey(d)}
                      className={`ats-chip${active ? ' active' : ''}${isToday ? ' today-mark' : ''}`}
                      onClick={() => onDateSelect(d)}
                    >
                      <div className="ats-cd">{DOW_S[d.getDay()]}</div>
                      <div className="ats-cn">{d.getDate()}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {isViewingPast && (
              <div className="ats-past-note" role="note">
                This date has passed. Changes you save here update the records for that day.
              </div>
            )}

            {/* Sport tabs — only shown once the club offers more than one
                sport (Config -> Sports & Courts). Switching tabs just
                filters which courts the grid below shows; court numbers
                stay globally unique across sports, so this is a pure
                client-side filter, not a separate data fetch. */}
            {multiSportGrid && (
              <div className="ats-sport-tabs" role="tablist">
                {config.sports.map(sport => (
                  <button
                    key={sport.id}
                    type="button"
                    role="tab"
                    aria-selected={activeSportId === sport.id}
                    className={`ats-sport-tab${activeSportId === sport.id ? ' active' : ''}`}
                    onClick={() => setActiveSportId(sport.id)}
                  >
                    {sport.icon ? `${sport.icon} ` : ''}{sport.name}
                  </button>
                ))}
              </div>
            )}

            <div className="ats-legend">
              <span><span className="ats-sw ats-sw-open"></span>Open</span>
              <span><span className="ats-sw ats-sw-booked"></span>Booked</span>
              <span><span className="ats-sw ats-sw-processing"></span>Processing</span>
              <span><span className="ats-sw ats-sw-passed"></span>Passed</span>
            </div>

            {multiSportGrid && courts.length === 0 && (
              <p className="ats-sport-empty">No courts assigned to this sport yet — add one in Config &rarr; Sports &amp; Courts.</p>
            )}

            {/* The horizontal scrollbar, above the grid rather than below it,
                and drawn rather than native so it shows on iOS and Android
                too. useTopScrollbar sizes and moves the thumb; the grid
                itself stays swipeable, so this is a redundant affordance and
                hidden from screen readers. */}
            <div className="ats-scrollbar" ref={trackRef} aria-hidden="true">
              <div className="ats-scrollbar-thumb" ref={thumbRef} />
            </div>

            <div className="ats-grid-container" ref={scrollerRef}>
              <div className="ats-slots-grid" style={{ '--ats-court-count': courts.length || 1 }}>
                <span className="ats-grid-spacer" aria-hidden="true" style={{ gridRow: 1, gridColumn: 1 }}></span>
                {courts.map((court, courtIndex) => (
                  <span
                    key={court}
                    className={`ats-court-header${courtAvailability[court] ? '' : ' closed'}`}
                    style={{ gridRow: 1, gridColumn: courtIndex + 2 }}
                  >
                    {getCourtLabel(config, court)}
                    {!courtAvailability[court] && <span className="ats-court-closed-tag">unavailable</span>}
                  </span>
                ))}

                {gridRows.map((row, rowIndex) => {
                  const timeSlot = row.slot;
                  const rowDate = row.dateStr;
                  const isNextDay = row.dayOffset === 1;
                  // Same two-line label as the guest grid: broken after the
                  // dash, meridiem set smaller and lowercase, and both ends
                  // named only on the four slots a day that cross a boundary.
                  const timeLabel = formatTimeSlotRangeParts(timeSlot);
                  const separator = rateSeparators.separators.get(rowIndex);
                  return (
                  // Keyed by date AND slot — one grid can show two dates.
                  <React.Fragment key={`${rowDate}|${timeSlot}`}>
                    {separator && (
                      <RateSeparatorRow
                        line={separator.line}
                        design={separatorDesign}
                        band={separator.band}
                        price={getRateSeparatorPriceText(config, courts, activeSportId || null, separator.band, rowDate)}
                        hours={formatRateBandHours(separator.band, gridRows, ' \u2013 ')}
                      />
                    )}
                    <span className="ats-time-cell" style={{ gridRow: rowLines[rowIndex], gridColumn: 1 }}>
                      <span className="ats-time-h">{timeLabel.start}</span>
                      <span className="ats-time-dash">{'\u2013'}</span>
                      <span className="ats-time-h">{timeLabel.end}</span>
                      {/* Row 2 — one meridiem under each end, on EVERY slot.
                          It used to be dropped when both ends matched, on the
                          grounds that "6:00 - 7:00 / AM AM" repeats itself;
                          the repetition turns out to be worth it, because a
                          lone suffix under the right-hand number reads as
                          belonging to that number rather than to the range. */}
                      <span className="ats-time-meridiem ats-time-meridiem-start">
                        {timeLabel.startMeridiem}
                      </span>
                      <span className="ats-time-meridiem ats-time-meridiem-end">
                        {timeLabel.endMeridiem}
                      </span>
                      {/* Only on rows that have crossed midnight: without it
                          a 1:00-2:00 AM row under Wednesday reads as
                          Wednesday morning, which is the wrong day. */}
                      {isNextDay && (
                        <span className="ats-time-nextday">{formatNextDayLabel(rowDate)}</span>
                      )}
                    </span>
                    {courts.map((court, courtIndex) => {
                      const spanKey = `${rowIndex}|${court}`;
                      // Swallowed by a merged block cell drawn on an earlier row.
                      if (mergedBlocks.covered.has(spanKey)) return null;
                      const blockSpan = mergedBlocks.starts.get(spanKey) || null;
                      // Every cell is placed explicitly: a merged block spans
                      // several rows, and auto-placement would push the cells
                      // after it into the wrong columns. Placed by line, not
                      // by span: a block crossing a rate change also covers
                      // the separator line in between.
                      const placement = {
                        gridRow: blockSpan
                          ? `${rowLines[rowIndex]} / ${rowLines[rowIndex + blockSpan.rowSpan - 1] + 1}`
                          : rowLines[rowIndex],
                        gridColumn: courtIndex + 2,
                      };
                      return renderCell(resolveCell(row, court), court, placement, blockSpan);
                    })}
                  </React.Fragment>
                  );
                })}
              </div>
            </div>

            {saveMessage && <div className="ats-toast">{saveMessage}</div>}
            {/* Cell edits are staged in `pendingChanges`, never written
                straight to Firebase — nothing leaves this screen until
                Save changes is pressed, so a mis-click is undoable and a
                batch of edits lands as one write (see handleSave). */}
            <div className="ats-savebar">
              <span className="ats-unsaved">
                {changeCount === 0
                  ? 'No unsaved changes.'
                  : `${changeCount} unsaved change${changeCount === 1 ? '' : 's'}.`}
              </span>
              <div className="ats-save-actions">
                <button
                  type="button"
                  className="ats-clear-btn"
                  disabled={changeCount === 0 || saving}
                  onClick={handleClearChanges}
                >
                  Clear changes
                </button>
                <button
                  type="button"
                  className="ats-save-btn"
                  disabled={changeCount === 0 || saving}
                  onClick={handleSave}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>

            {/* Hidden unless the club has switched Open Play on (Config ->
                System). `=== true` rather than `!== false`: the key is
                absent on every config saved before the setting existed, and
                those clubs should stay as they are until someone opts in. */}
            {openPlayEnabled && (
              <div className="ats-savebar">
                <button
                  type="button"
                  className="ats-host-openplay-btn"
                  onClick={() => { setEditingOpenPlaySession(null); setShowHostOpenPlay(true); }}
                >
                  Host an Open Play session
                </button>
              </div>
            )}
          </div>

          {/* The list itself always renders, so a club that switches Open
              Play off can still open and delete sessions it already hosted
              — hiding them would strand live sessions with no way to reach
              them. Only the empty state is gated: with the feature off and
              nothing hosted, the component returns null rather than leaving
              an empty panel on a grid that has nothing to do with it. */}
          <OpenPlaySessionsSection
            sportId={activeSportId}
            variant="admin"
            showEmptyState={openPlayEnabled}
            onCardClick={(clickedSession) => { setEditingOpenPlaySession(clickedSession); setShowHostOpenPlay(true); }}
          />
        </div>

        <div className="ats-sidebar">
          <div className="ats-side-card">
            <h3>Day's All Booking Summary</h3>
            <div className="ats-summary-date">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
              <span>{selectedDate ? format(selectedDate, 'MMM d, yyyy') : '—'}</span>
            </div>
            <button type="button" className="ats-copy-btn" disabled={summaryCount === 0} onClick={handleCopyDetails}>
              Copy Details
            </button>
            {/* Says what's there before the admin clicks — a disabled button
                with no explanation reads as broken rather than as "nothing
                confirmed yet". */}
            <p className="ats-summary-count">
              {summaryCount === 0
                ? 'No confirmed reservations for this day.'
                : `${summaryCount} confirmed reservation${summaryCount === 1 ? '' : 's'}.`}
            </p>
            {copyMessage && <div className="ats-toast ats-toast-side">{copyMessage}</div>}
          </div>

          {/* Shareable images of this day's open slots for Facebook /
              Instagram / group chats — see AvailabilityImageModal. */}
          <div className="ats-side-card">
            <h3>Share availability</h3>
            <button
              type="button"
              className="ats-copy-btn"
              disabled={!selectedDate || courts.length === 0}
              onClick={handleOpenAvailabilityImages}
            >
              Generate Images
            </button>
            <p className="ats-summary-count">Create images of this day&apos;s open slots to post or send.</p>
          </div>

          <div className="ats-side-card">
            <div className="ats-queue-head">
              <h3>Reservation queue</h3>
              <span className="ats-queue-count">{queueFiltered.length}</span>
            </div>

            <div className="ats-queue-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={queueDateFilter === 'current'}
                className={`ats-queue-tab${queueDateFilter === 'current' ? ' active' : ''}`}
                onClick={() => setQueueDateFilter('current')}
              >Current Date</button>
              <button
                type="button"
                role="tab"
                aria-selected={queueDateFilter === 'all'}
                className={`ats-queue-tab${queueDateFilter === 'all' ? ' active' : ''}`}
                onClick={() => setQueueDateFilter('all')}
              >All Dates</button>
            </div>

            <p className="ats-queue-hint">Reservations awaiting your verification. Tap one to review the receipt, then Confirm or Decline.</p>

            {queueFiltered.length === 0 ? (
              <p className="ats-queue-empty">
                {queueDateFilter === 'current'
                  ? <>No pending reservations for this date.<br />Try "All Dates" to see the rest.</>
                  : <>No pending reservations.<br />You're all caught up.</>}
              </p>
            ) : (
              <>
                <div className="ats-queue-list">
                  {pagedQueue.map(booking => {
                    // booking.dates covers every date in this reservation
                    // (see groupBookingRecords) — just one, for an ordinary
                    // single-date booking.
                    const dates = booking.dates && booking.dates.length > 0 ? booking.dates : [{ date: booking.date, timeSlots: booking.timeSlots }];
                    const hours = dates.reduce((total, d) => total + Object.values(d.timeSlots || {}).reduce(
                      (sum, courtsForSlot) => sum + (Array.isArray(courtsForSlot) ? courtsForSlot.length : Object.keys(courtsForSlot).length),
                      0
                    ), 0);
                    const queueType = queueCardType(booking);
                    return (
                      <button
                        type="button"
                        key={booking.id}
                        className="ats-queue-card"
                        onClick={() => onQueueCardClick(booking)}
                      >
                        <div className="ats-qc-top">
                          <span className="ats-qc-id">{booking.confirmationId}</span>
                          <span className="ats-qc-amt">{formatMoney(Number(booking.paymentAmount || 0), config)}</span>
                        </div>
                        <div className="ats-qc-name">{booking.userName}</div>
                        <div className="ats-qc-phone">{booking.phone}</div>
                        <div className="ats-qc-bottom">
                          {/* What KIND of thing this is decides what the
                              admin is agreeing to — a court for an hour, a
                              whole venue block, or players on a session —
                              and the three used to be indistinguishable
                              until you opened one. */}
                          <span className={`ats-qc-type ats-qc-type-${queueType.cls}`}>{queueType.label}</span>
                          {/* An Open Play join books no court-hours; its
                              size is how many players are coming. */}
                          <span className="ats-qc-hours">
                            {booking.isOpenPlayJoin
                              ? `${booking.numPlayers} player${Number(booking.numPlayers) === 1 ? '' : 's'}`
                              : `${hours} hr${hours === 1 ? '' : 's'} total`}
                          </span>
                          {hasReceipt(booking) && <span className="ats-qc-receipt">RECEIPT</span>}
                        </div>
                        {dates.length > 1
                          ? <div className="ats-qc-date">{dates.length} dates</div>
                          : dates[0]?.date && <div className="ats-qc-date">{format(new Date(`${dates[0].date}T00:00:00`), 'MMM d')}</div>}
                      </button>
                    );
                  })}
                </div>

                {queueTotalPages > 1 && (
                  <div className="ats-queue-pager">
                    <button
                      type="button"
                      className="ats-pager-btn"
                      disabled={queuePage === 1}
                      onClick={() => setQueuePage(p => Math.max(1, p - 1))}
                    >&lsaquo;</button>
                    <span className="ats-pager-label">Page {queuePage} of {queueTotalPages}</span>
                    <button
                      type="button"
                      className="ats-pager-btn"
                      disabled={queuePage === queueTotalPages}
                      onClick={() => setQueuePage(p => Math.min(queueTotalPages, p + 1))}
                    >&rsaquo;</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AdminHostOpenPlayModal
        isOpen={showHostOpenPlay}
        onClose={() => { setShowHostOpenPlay(false); setEditingOpenPlaySession(null); }}
        sportId={activeSportId || config?.sports?.[0]?.id}
        initialDate={selectedDate}
        session={editingOpenPlaySession}
        onHosted={(mode) => setSaveMessage(mode === 'edit' ? 'Open Play session updated.' : 'Open Play session hosted.')}
      />

      <AvailabilityImageModal
        isOpen={!!availabilitySnapshot}
        snapshot={availabilitySnapshot}
        loadRangeSnapshots={loadAvailabilityRange}
        onClose={() => setAvailabilitySnapshot(null)}
      />

      <CloseSlotReasonModal
        isOpen={!!closePrompt}
        slotLabel={closePrompt
          ? `${format(new Date(`${closePrompt.rowDate}T00:00:00`), 'MMM d, yyyy')} · ${getCourtLabel(config, closePrompt.court)} · ${formatTimeSlotRange(closePrompt.timeSlot)}`
          : ''}
        onConfirm={handleCloseReasonConfirm}
        onCancel={() => setClosePrompt(null)}
      />
    </div>
  );
});

export default AdminTimeSlotSelection;
